import { Button, Header, LoadingSpinner } from '../../Generic'
import { observer } from 'mobx-react'
import { QRCodeSVG } from 'qrcode.react'
import { useContext } from 'react'
import { StoreContext } from '@store/Root.store'

import '../SwitchToMobile.css'
import { DividerWithText } from '@/components/DividerWithText/DividerWithText'
import { cx } from '@/utils/cx'
import { SendSmsStatus } from './SendSmsStatus'
import { useScanQR } from './useScanQR'

interface ScanQRWithHowtoProps {
  nextStep: () => void
  stayUrl?: string
  smsUrl?: string
  smsMaxAttempts: number
  isSmsEnabled?: boolean
  isCameraAvailable?: boolean
  retrySeconds?: number
  getProceedLinkUrl?: string
  isMobileSessionStartedUrl?: string
  closeDesktopSessionUrl?: string
}

export const ScanQRWithHowto = observer(
  ({
    nextStep,
    stayUrl,
    smsUrl,
    smsMaxAttempts,
    isSmsEnabled,
    retrySeconds,
    getProceedLinkUrl,
    isMobileSessionStartedUrl,
    closeDesktopSessionUrl,
  }: ScanQRWithHowtoProps) => {
    const store = useContext(StoreContext)
    const { switchToMobile: trans } = store.TranslationsState.translationsNew

    const {
      QRCodeUrl,
      isRedirecting,
      disabled,
      counting,
      isSendSmsButtonVisible,
      sendSmsCount,
      countdownProgress,
      sendSms,
      stayOnDevice,
    } = useScanQR({
      nextStep,
      stayUrl,
      smsUrl,
      isSmsEnabled,
      retrySeconds,
      getProceedLinkUrl,
      isMobileSessionStartedUrl,
      closeDesktopSessionUrl,
    })

    return QRCodeUrl ? (
      <>
        <Header>{trans.howToContinue?.header}</Header>

        <div className="layout__vertically-centered-container layout__margined m-0-0-48">
          <p className="generic__paragraph-centered m-0">{trans.scanQr.description2}</p>
          <div className="layout__margined m-0-0-24" />
          <div className="generic__paragraph-centered">
            <QRCodeSVG
              value={QRCodeUrl}
              href={QRCodeUrl}
              data-testid="qrCode"
              role="img"
              aria-label={trans.scanQr.qrCode}
              title={trans.scanQr.qrCode}
            />
          </div>
        </div>

        {isSmsEnabled && (
          <div className="layout__vertically-centered-container layout__margined m-0-0-48">
            <p className="generic__paragraph-centered">{trans.scanQr.sendSms}</p>
            <div className="layout__margined m-0-0-12" />
            <SendSmsStatus
              disabled={disabled}
              counting={counting}
              isRedirecting={isRedirecting}
              isSendSmsButtonVisible={isSendSmsButtonVisible}
              sendSmsCount={sendSmsCount}
              smsMaxAttempts={smsMaxAttempts}
              countdownProgress={countdownProgress}
              sendSms={sendSms}
            />
          </div>
        )}

        <DividerWithText text={`${trans.howToContinue?.or ?? 'or'}`} margin="30px 0" width="100%" />

        <div className="layout__vertically-centered-container ">
          <p className="generic__paragraph-centered">{trans.scanQr.stayOnThisDevice}</p>
          <div className="layout__margined m-0-0-12" />
          <Button
            disabled={disabled || counting || isRedirecting}
            onClick={stayOnDevice}
            className={cx('button--secondary', 'button--proceed')}
            tabIndex={0}
          >
            {isRedirecting && <LoadingSpinner inButton />}
            <span
              className={cx(
                'generic__button-text',
                isRedirecting && 'generic__button-text--loading'
              )}
              data-testId="proceed"
            >
              {trans.scanQr.stayOnThisDeviceBtn}
            </span>
          </Button>
        </div>
      </>
    ) : (
      <LoadingSpinner />
    )
  }
)
